import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions as configuredFunctions } from '../config/firebase';
import { addDaysToKey, parseDateKey, toDateKey } from './calendarHelpers';
import { normalizeMemberPermissionsMap } from './tourMemberPermissionsService';

export const DEFAULT_RIDER_REMINDER = {
  enabled: false,
  daysBefore: 1,
  inApp: true,
  email: true,
  modules: ['RIDER'],
};

export const DAY_SCHEDULE_MODES = {
  INHERIT: 'inherit',
  CUSTOM: 'custom',
  DISABLED: 'disabled',
};

export const SHARE_SCHEDULE_MODULE_OPTIONS = [
  'RIDER',
  'STAGE',
  'SETLIST',
  'GUEST LIST',
  'SCHEDULE',
  'CONTACTS',
  'FILES',
  'DAY SHEET',
  'LODGING',
];

function generateScheduleId() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint8Array(8);
    crypto.getRandomValues(arr);
    return `sch_${Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `sch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeSendSchedule(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const dateKey = String(raw.dateKey || '').trim();
  const sendDateKey = String(raw.sendDateKey || '').trim();
  if (!dateKey || !parseDateKey(dateKey)) return null;
  const resolvedSend = sendDateKey && parseDateKey(sendDateKey)
    ? sendDateKey
    : addDaysToKey(dateKey, -Math.max(1, Number(raw.daysBefore) || 1));
  const modules = Array.isArray(raw.modules)
    ? raw.modules.map((m) => String(m || '').trim()).filter(Boolean)
    : ['RIDER'];
  return {
    id: String(raw.id || generateScheduleId()),
    kind: String(raw.kind || 'manual'),
    linkId: String(raw.linkId || '').trim(),
    dateKey,
    sendDateKey: resolvedSend,
    daysBefore: Math.max(0, Number(raw.daysBefore) || 1),
    modules,
    label: String(raw.label || '').trim() || modules.join(', '),
    channels: {
      email: raw.channels?.email !== false,
      inApp: raw.channels?.inApp !== false,
    },
    recipientEmails: Array.isArray(raw.recipientEmails)
      ? raw.recipientEmails.map((e) => String(e || '').trim().toLowerCase()).filter(Boolean)
      : [],
    status: String(raw.status || 'scheduled'),
    sentAt: raw.sentAt || null,
  };
}

export function normalizeDayScheduleOverride(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const mode = String(raw.mode || DAY_SCHEDULE_MODES.INHERIT);
  const modules = Array.isArray(raw.modules)
    ? raw.modules.map((m) => String(m || '').trim()).filter(Boolean)
    : null;
  return {
    mode: [DAY_SCHEDULE_MODES.INHERIT, DAY_SCHEDULE_MODES.CUSTOM, DAY_SCHEDULE_MODES.DISABLED].includes(mode)
      ? mode
      : DAY_SCHEDULE_MODES.INHERIT,
    daysBefore:
      raw.daysBefore != null && raw.daysBefore !== ''
        ? Math.max(0, Number(raw.daysBefore) || 1)
        : null,
    modules: modules?.length ? modules : null,
    channels:
      raw.channels && typeof raw.channels === 'object'
        ? {
            email: raw.channels.email !== false,
            inApp: raw.channels.inApp !== false,
          }
        : null,
    recipientEmails: Array.isArray(raw.recipientEmails)
      ? raw.recipientEmails.map((e) => String(e || '').trim().toLowerCase()).filter(Boolean)
      : [],
    label: String(raw.label || '').trim(),
  };
}

export function normalizeTourShareSettings(data = {}) {
  const dayScheduleOverrides = {};
  if (data.dayScheduleOverrides && typeof data.dayScheduleOverrides === 'object') {
    Object.entries(data.dayScheduleOverrides).forEach(([dateKey, raw]) => {
      const normalized = normalizeDayScheduleOverride(raw);
      if (normalized && parseDateKey(dateKey)) dayScheduleOverrides[dateKey] = normalized;
    });
  }
  return {
    managerIds: Array.isArray(data.managerIds) ? data.managerIds : [],
    setlistModeratorId: String(data.setlistModeratorId || '').trim(),
    memberPermissions: normalizeMemberPermissionsMap(data.memberPermissions),
    riderReminder: {
      ...DEFAULT_RIDER_REMINDER,
      modules: DEFAULT_RIDER_REMINDER.modules,
      ...(data.riderReminder && typeof data.riderReminder === 'object' ? data.riderReminder : {}),
    },
    dayScheduleOverrides,
    sendSchedules: (Array.isArray(data.sendSchedules) ? data.sendSchedules : [])
      .map(normalizeSendSchedule)
      .filter(Boolean),
    reminderSentLog: data.reminderSentLog && typeof data.reminderSentLog === 'object' ? data.reminderSentLog : {},
  };
}

export function subscribeTourShareSettings(db, ownerId, tourId, callback) {
  if (!ownerId || !tourId) {
    callback(normalizeTourShareSettings());
    return () => {};
  }
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? snap.data() || {} : {};
      callback(normalizeTourShareSettings(data));
    },
    () => callback(normalizeTourShareSettings())
  );
}

export async function saveRiderReminderSettings(db, ownerId, tourId, riderReminder) {
  if (!ownerId || !tourId) throw new Error('Missing tour.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  const normalized = { ...DEFAULT_RIDER_REMINDER, ...(riderReminder || {}) };
  await setDoc(
    ref,
    {
      tourId,
      riderReminder: normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return normalized;
}

export async function upsertSendSchedule(db, ownerId, tourId, schedule) {
  if (!ownerId || !tourId) throw new Error('Missing tour.');
  const normalized = normalizeSendSchedule(schedule);
  if (!normalized) throw new Error('Invalid schedule.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  const snap = await getDoc(ref);
  const settings = normalizeTourShareSettings(snap.exists() ? snap.data() : {});
  const existing = settings.sendSchedules.filter((s) => s.id !== normalized.id);
  await setDoc(
    ref,
    {
      tourId,
      sendSchedules: [...existing, normalized],
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return normalized;
}

export async function deleteSendSchedule(db, ownerId, tourId, scheduleId) {
  if (!ownerId || !tourId || !scheduleId) throw new Error('Missing schedule.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  const snap = await getDoc(ref);
  const settings = normalizeTourShareSettings(snap.exists() ? snap.data() : {});
  await setDoc(
    ref,
    {
      tourId,
      sendSchedules: settings.sendSchedules.filter((s) => s.id !== scheduleId),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function saveDayScheduleOverride(db, ownerId, tourId, dateKey, override) {
  if (!ownerId || !tourId || !dateKey) throw new Error('Missing day.');
  const normalized = normalizeDayScheduleOverride(override);
  if (!normalized) throw new Error('Invalid day schedule.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  const snap = await getDoc(ref);
  const settings = normalizeTourShareSettings(snap.exists() ? snap.data() : {});
  const next = { ...(settings.dayScheduleOverrides || {}) };

  if (normalized.mode === DAY_SCHEDULE_MODES.INHERIT && !normalized.label && !normalized.recipientEmails?.length) {
    delete next[dateKey];
  } else {
    next[dateKey] = normalized;
  }

  await setDoc(
    ref,
    {
      tourId,
      dayScheduleOverrides: next,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return next[dateKey] || null;
}

export async function clearDayScheduleOverride(db, ownerId, tourId, dateKey) {
  if (!ownerId || !tourId || !dateKey) throw new Error('Missing day.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  const snap = await getDoc(ref);
  const settings = normalizeTourShareSettings(snap.exists() ? snap.data() : {});
  const next = { ...(settings.dayScheduleOverrides || {}) };
  delete next[dateKey];
  await setDoc(
    ref,
    {
      tourId,
      dayScheduleOverrides: next,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Resolved schedule config for one show day (global + optional override). */
export function resolveDaySchedule(settings, dateKey, { isShowDay = true } = {}) {
  const global = settings?.riderReminder || DEFAULT_RIDER_REMINDER;
  const override = settings?.dayScheduleOverrides?.[dateKey] || null;
  const globalModules = globalModulesList(global);

  if (!isShowDay) {
    return {
      enabled: false,
      isShowDay: false,
      mode: DAY_SCHEDULE_MODES.INHERIT,
      isException: false,
      usesGlobal: true,
    };
  }

  if (override?.mode === DAY_SCHEDULE_MODES.DISABLED) {
    return {
      enabled: false,
      isShowDay: true,
      mode: DAY_SCHEDULE_MODES.DISABLED,
      isException: true,
      usesGlobal: false,
      daysBefore: null,
      modules: globalModules,
      channels: { email: global.email !== false, inApp: global.inApp !== false },
      sendDateKey: null,
    };
  }

  const globalEnabled = Boolean(global.enabled);
  const usesCustom = override?.mode === DAY_SCHEDULE_MODES.CUSTOM;
  const enabled = usesCustom ? true : globalEnabled;

  if (!enabled) {
    return {
      enabled: false,
      isShowDay: true,
      mode: DAY_SCHEDULE_MODES.INHERIT,
      isException: false,
      usesGlobal: true,
      daysBefore: Math.max(1, Number(global.daysBefore) || 1),
      modules: globalModules,
      channels: { email: global.email !== false, inApp: global.inApp !== false },
      sendDateKey: null,
    };
  }

  const daysBefore = usesCustom && override?.daysBefore != null
    ? Math.max(0, Number(override.daysBefore) || 1)
    : Math.max(1, Number(global.daysBefore) || 1);
  const modules = usesCustom && override?.modules?.length ? override.modules : globalModules;
  const channels = usesCustom && override?.channels
    ? override.channels
    : { email: global.email !== false, inApp: global.inApp !== false };

  return {
    enabled: true,
    isShowDay: true,
    mode: usesCustom ? DAY_SCHEDULE_MODES.CUSTOM : DAY_SCHEDULE_MODES.INHERIT,
    isException: usesCustom,
    usesGlobal: !usesCustom,
    daysBefore,
    modules,
    channels,
    recipientEmails: override?.recipientEmails || [],
    label: override?.label || '',
    sendDateKey: addDaysToKey(dateKey, -daysBefore),
  };
}

function daysBeforeLabel(n) {
  const num = Math.max(0, Number(n) || 0);
  if (num === 0) return 'on the show day';
  if (num === 1) return '1 day before';
  return `${num} days before`;
}

function channelsLabel(channels = {}) {
  const parts = [];
  if (channels.inApp !== false) parts.push('in-app');
  if (channels.email !== false) parts.push('email');
  return parts.length ? parts.join(' and ') : 'no notifications';
}

function globalModulesList(global) {
  return Array.isArray(global?.modules) && global.modules.length
    ? global.modules
    : DEFAULT_RIDER_REMINDER.modules;
}

/** One-line summary of tour-wide scheduled send defaults. */
export function formatGlobalTourScheduleSummary(settings) {
  const global = settings?.riderReminder || DEFAULT_RIDER_REMINDER;
  if (!global.enabled) {
    return 'Tour scheduled sends are off globally. Show days follow this unless you set a custom schedule on an individual day.';
  }
  const when = daysBeforeLabel(global.daysBefore);
  const via = channelsLabel({ email: global.email !== false, inApp: global.inApp !== false });
  const mods = globalModulesList(global).join(', ');
  return `Tour default: notify ${when} via ${via} for ${mods || 'shared sections'}.`;
}

/** Human-readable status for the day sidebar strip. */
export function formatDayScheduleStatusMessage(settings, dateKey, { isShowDay = true } = {}) {
  const parts = getDayScheduleStatusParts(settings, dateKey, { isShowDay });
  if (parts.variant === 'enabled') {
    return `${parts.beforeSendDate}${parts.sendDateLabel}${parts.afterSendDate}`;
  }
  return parts.text;
}

/** Compact enabled/disabled label for sidebar chip. */
export function getDayScheduleDisplayStatus(settings, dateKey, { isShowDay = true } = {}) {
  const resolved = resolveDaySchedule(settings, dateKey, { isShowDay });
  const enabled = Boolean(resolved.enabled && resolved.isShowDay);
  return {
    enabled,
    statusWord: enabled ? 'Enabled' : 'Disabled',
    statusColor: enabled ? '#22c55e' : '#ef4444',
    resolved,
  };
}

export function listDayScheduleExceptions(settings, showDateKeys = []) {
  const overrides = settings?.dayScheduleOverrides || {};
  return showDateKeys
    .filter((dk) => overrides[dk] && overrides[dk].mode !== DAY_SCHEDULE_MODES.INHERIT)
    .map((dateKey) => ({
      dateKey,
      override: overrides[dateKey],
      resolved: resolveDaySchedule(settings, dateKey, { isShowDay: true }),
    }));
}

/** Auto rider reminders — one per show day, respecting day overrides. */
export function buildRiderReminderSchedules(settings, showDateKeys = []) {
  if (!settings?.riderReminder && !settings?.dayScheduleOverrides) return [];

  return showDateKeys
    .filter((dk) => parseDateKey(dk))
    .map((dateKey) => {
      const resolved = resolveDaySchedule(settings, dateKey, { isShowDay: true });
      if (!resolved.enabled) return null;

      const daysBefore = resolved.daysBefore ?? 1;
      return {
        id: `rider-reminder__${dateKey}`,
        kind: resolved.isException ? 'day_override' : 'rider_reminder',
        linkId: '',
        dateKey,
        sendDateKey: addDaysToKey(dateKey, -daysBefore),
        daysBefore,
        modules: resolved.modules || ['RIDER'],
        label: resolved.label || (resolved.isException ? 'Custom day schedule' : 'Rider send reminder'),
        channels: resolved.channels || { email: true, inApp: true },
        recipientEmails: resolved.recipientEmails || [],
        status: 'scheduled',
      };
    })
    .filter(Boolean);
}

export function getEffectiveSendSchedules(settings, showDateKeys = []) {
  const manual = (settings?.sendSchedules || []).filter(
    (s) => s.status !== 'sent' && s.status !== 'cancelled' && s.kind !== 'rider_reminder'
  );
  const auto = buildRiderReminderSchedules(settings, showDateKeys);
  return [...auto, ...manual];
}

export function buildShareScheduleIndicators({ settings, showDateKeys = [] }) {
  const byDate = {};
  const add = (dateKey, item) => {
    if (!dateKey || !parseDateKey(dateKey)) return;
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(item);
  };

  getEffectiveSendSchedules(settings, showDateKeys).forEach((s) => {
    add(s.sendDateKey, {
      scheduleId: s.id,
      type: 'send',
      kind: s.kind,
      label: s.label,
      modules: s.modules,
      showDateKey: s.dateKey,
      channels: s.channels,
    });
    add(s.dateKey, {
      scheduleId: s.id,
      type: 'show',
      kind: s.kind,
      label: s.label,
      modules: s.modules,
      sendDateKey: s.sendDateKey,
      channels: s.channels,
    });
  });

  return byDate;
}

export function getSchedulesForLink(link, settings, showDateKeys = []) {
  if (!link) return [];
  const all = getEffectiveSendSchedules(settings, showDateKeys);
  return all.filter(
    (s) =>
      (s.linkId && link.id && s.linkId === link.id) ||
      (link.type === 'day' && link.dateKey && s.dateKey === link.dateKey)
  );
}

export function formatScheduleDateLabel(dateKey) {
  const dt = parseDateKey(dateKey);
  if (!dt) return dateKey || '';
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Long weekday + calendar date for scheduled send copy. */
export function formatScheduleSendDateLabel(dateKey) {
  const dt = parseDateKey(dateKey);
  if (!dt) return dateKey || '';
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Structured status copy for rich UI (send date highlighted separately). */
export function getDayScheduleStatusParts(settings, dateKey, { isShowDay = true } = {}) {
  const global = settings?.riderReminder || DEFAULT_RIDER_REMINDER;
  const resolved = resolveDaySchedule(settings, dateKey, { isShowDay });
  const globalWhen = daysBeforeLabel(global.daysBefore);
  const globalVia = channelsLabel({ email: global.email !== false, inApp: global.inApp !== false });
  const globalMods = globalModulesList(global).join(', ');

  if (!isShowDay) {
    return {
      variant: 'plain',
      text: 'Not a scheduled show day — per-day schedule settings are available on tour show dates.',
    };
  }

  if (resolved.mode === DAY_SCHEDULE_MODES.DISABLED) {
    return {
      variant: 'plain',
      text: 'Scheduled send is disabled for this day (exception). Other show days still use the tour default.',
    };
  }

  if (!global.enabled && resolved.mode !== DAY_SCHEDULE_MODES.CUSTOM) {
    return {
      variant: 'plain',
      text: 'Tour scheduled sends are off globally. Open Tour settings to enable for all show days, or choose Custom below for this day only.',
    };
  }

  if (!resolved.enabled) {
    return {
      variant: 'plain',
      text: 'Scheduled send is off for this day.',
    };
  }

  const when = daysBeforeLabel(resolved.daysBefore);
  const via = channelsLabel(resolved.channels);
  const mods = (resolved.modules || []).join(', ');
  const sendDateLabel = formatScheduleSendDateLabel(resolved.sendDateKey);

  if (resolved.usesGlobal) {
    return {
      variant: 'enabled',
      sendDateLabel,
      beforeSendDate: `Scheduled send enabled for this day — notifies ${when} on `,
      afterSendDate: ` (${via}, tour default) for ${mods || globalMods || 'shared sections'}.`,
    };
  }

  return {
    variant: 'enabled',
    sendDateLabel,
    beforeSendDate: `Scheduled send enabled — custom for this day: notifies ${when} on `,
    afterSendDate: ` via ${via} for ${mods || 'shared sections'} (tour default is ${globalWhen}, ${globalVia}).`,
  };
}

export function formatScheduleSummary(schedule) {
  if (!schedule) return '';
  const mods = (schedule.modules || []).join(', ');
  const when = formatScheduleDateLabel(schedule.sendDateKey);
  const show = formatScheduleDateLabel(schedule.dateKey);
  if (schedule.kind === 'rider_reminder') {
    return `Reminder ${when} · rider for ${show}`;
  }
  const channels = [];
  if (schedule.channels?.email) channels.push('email');
  if (schedule.channels?.inApp) channels.push('in-app');
  return `Send ${when} · ${mods}${channels.length ? ` (${channels.join(' + ')})` : ''}`;
}

async function sendScheduleReminderEmail(recipient, emailData) {
  const handleEmail = httpsCallable(configuredFunctions || getFunctions(), 'handleEmail');
  await handleEmail({
    template: 'tour_share_schedule_reminder',
    recipient: String(recipient || '').trim().toLowerCase(),
    emailData,
  });
}

export async function markReminderSent(db, ownerId, tourId, scheduleId, { manualSchedule = false } = {}) {
  if (!ownerId || !tourId || !scheduleId) return;
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  if (manualSchedule) {
    const snap = await getDoc(ref);
    const settings = normalizeTourShareSettings(snap.exists() ? snap.data() : {});
    const next = settings.sendSchedules.map((s) =>
      s.id === scheduleId ? { ...s, status: 'sent', sentAt: new Date().toISOString() } : s
    );
    await updateDoc(ref, { sendSchedules: next, updatedAt: serverTimestamp() });
    return;
  }
  await setDoc(
    ref,
    {
      [`reminderSentLog.${scheduleId}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

function isReminderAlreadySent(settings, scheduleId) {
  return Boolean(settings?.reminderSentLog?.[scheduleId]);
}

/** Client-side check when calendar opens — notifies tour owner/managers of due reminders. */
export async function processDueShareReminders({
  db,
  ownerId,
  ownerEmail,
  ownerName,
  tourId,
  tourName,
  settings,
  showDateKeys = [],
  onInApp,
}) {
  if (!ownerId || !tourId || !settings) return [];
  const todayKey = toDateKey(new Date());
  if (!todayKey) return [];

  const due = getEffectiveSendSchedules(settings, showDateKeys).filter((s) => {
    if (s.sendDateKey !== todayKey) return false;
    if (s.kind === 'rider_reminder') return !isReminderAlreadySent(settings, s.id);
    return s.status === 'scheduled';
  });

  const processed = [];
  for (const schedule of due) {
    const showLabel = formatScheduleDateLabel(schedule.dateKey);
    const message =
      schedule.kind === 'rider_reminder'
        ? `Reminder: send the rider for ${showLabel} (${tourName || 'tour'}).`
        : `Scheduled share today: ${(schedule.modules || []).join(', ')} for ${showLabel}.`;

    if (schedule.channels?.inApp !== false) {
      onInApp?.(message, schedule);
    }

    if (schedule.channels?.email !== false) {
      const emailTargets = new Set();
      if (ownerEmail) emailTargets.add(String(ownerEmail).trim().toLowerCase());
      if (schedule.kind !== 'rider_reminder') {
        (schedule.recipientEmails || []).forEach((e) => {
          const normalized = String(e || '').trim().toLowerCase();
          if (normalized) emailTargets.add(normalized);
        });
      }
      for (const recipient of emailTargets) {
        try {
          await sendScheduleReminderEmail(recipient, {
            ownerName: ownerName || 'Tour team',
            tourName: tourName || 'Tour',
            showDateLabel: showLabel,
            sendDateLabel: formatScheduleDateLabel(schedule.sendDateKey),
            modules: (schedule.modules || []).join(', '),
            message,
            kind: schedule.kind,
          });
        } catch (err) {
          console.warn('processDueShareReminders email', err);
        }
      }
    }

    await markReminderSent(db, ownerId, tourId, schedule.id, {
      manualSchedule: schedule.kind === 'manual',
    });
    processed.push(schedule);
  }

  return processed;
}
